import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Building2, ShieldCheck } from "lucide-react";
import { CATEGORY_LABEL } from "@/components/network/TierBadge";
import { AFRICAN_COUNTRIES } from "@/lib/countries";

export default function OrganisationRegister() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({
    name: "", category: "workshop", country: "", city: "",
    website: "", contact_email: user?.email || "", contact_phone: "",
    registration_number: "", address: "", description: "",
  });
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please sign in first"); navigate("/login"); return; }
    setBusy(true);
    try {
      const r = await api.post("/network/organisations", f);
      toast.success("Application received — pending admin verification");
      navigate(`/network/${r.data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally { setBusy(false); }
  };

  return (
    <div>
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 py-10">
        <div className="flex items-center gap-3 mb-6">
          <div className="h-12 w-12 rounded-sm bg-primary/10 grid place-items-center">
            <Building2 className="h-6 w-6 text-primary" />
          </div>
          <div>
            <div className="kicker">Trust Network</div>
            <h1 className="display text-3xl">Register your organisation</h1>
          </div>
        </div>
        <p className="text-muted-foreground text-sm mb-8">
          Join Africa's independent trust network of vetted dealerships, banks, insurers,
          workshops and government agencies. Applications are reviewed by THOM Verify™ within 48 hours.
        </p>

        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 border border-border p-6 bg-card" data-testid="org-register-form">
          <Row label="Organisation name *"><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="org-name-input" /></Row>
          <Row label="Category *">
            <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
              <SelectTrigger data-testid="org-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORY_LABEL).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </Row>
          <Row label="Country *">
            <Select value={f.country} onValueChange={(v) => setF({ ...f, country: v })}>
              <SelectTrigger data-testid="org-country-select"><SelectValue placeholder="Select country" /></SelectTrigger>
              <SelectContent>
                {AFRICAN_COUNTRIES.map((c) => <SelectItem key={c.iso} value={c.name}>{c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Row>
          <Row label="City"><Input value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} data-testid="org-city-input" /></Row>
          <Row label="Website"><Input value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} placeholder="https://" /></Row>
          <Row label="Registration number"><Input value={f.registration_number} onChange={(e) => setF({ ...f, registration_number: e.target.value })} /></Row>
          <Row label="Contact email"><Input type="email" value={f.contact_email} onChange={(e) => setF({ ...f, contact_email: e.target.value })} data-testid="org-email-input" /></Row>
          <Row label="Contact phone"><Input value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: e.target.value })} /></Row>
          <div className="md:col-span-2">
            <Label className="text-xs mono uppercase tracking-widest">Address</Label>
            <Input value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })} />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs mono uppercase tracking-widest">About</Label>
            <Textarea rows={4} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="org-desc-input" />
          </div>
          <div className="md:col-span-2 flex items-center gap-2 border border-primary/30 bg-primary/5 p-3 rounded-sm text-xs">
            <ShieldCheck className="h-4 w-4 text-primary" />
            Applications are subject to verification. Providing false information may result in permanent exclusion from the Trust Network.
          </div>
          <div className="md:col-span-2 flex justify-end gap-2">
            <Button type="submit" disabled={busy} data-testid="org-submit-btn">{busy ? "Submitting…" : "Submit application"}</Button>
          </div>
        </form>
      </div>
      <Footer />
    </div>
  );
}

const Row = ({ label, children }) => (
  <div>
    <Label className="text-xs mono uppercase tracking-widest">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);
